import type {
  DataAlterationGuardAsk,
  DataAlterationGuardName,
  DataAlterationGuardOptions,
  DataAlterationGuardRequest,
  DataAlterationGuardResult,
  DataAlterationGuardVerdict,
  ResetToDefaultVerdict,
  UnreviewConfirmationVerdict,
} from './data-alteration-guard.types'

import {
  ALLOWED_BY_GUARD,
  GUARD_MARKER,
} from './data-alteration-guard.constants'
import { v4 as generateUuid } from 'uuid'

const publishedGuards = new Set<DataAlterationGuardName>()

// Asks whether the user accepts that this change outdates the reviews of the
// template.
export function askUnreviewConfirmation(
  options?: DataAlterationGuardOptions
): Promise<UnreviewConfirmationVerdict> {
  return guardDataAlteration('unreview-confirmation', { options })
}

// One channel serves every caller and every guard, so the request carries a
// uuid. The wait has no deadline: the user reads the modal for as long as they
// like.
export function guardDataAlteration<G extends DataAlterationGuardName>(
  guard: G,
  ask: Omit<DataAlterationGuardAsk<G>, 'guard'>
): Promise<DataAlterationGuardVerdict<G>> {
  // Nobody is mounted to answer this guard. The policy that mounts the decider
  // also decides who may write, so a caller that reaches here may proceed.
  if (!guardIsPublished(guard)) return Promise.resolve(ALLOWED_BY_GUARD[guard])

  const requestId = generateUuid()

  return new Promise((resolve) => {
    const receiveVerdict = (result: DataAlterationGuardResult): void => {
      if (result.requestId !== requestId || result.guard !== guard) return

      window.eventHub.$off('data-alteration-guard-result', receiveVerdict)
      // TypeScript cannot tie a generic G to one member of the result union.
      // The check above does: the result carries this request's guard.
      resolve(verdictOf(result) as DataAlterationGuardVerdict<G>)
    }

    window.eventHub.$on('data-alteration-guard-result', receiveVerdict)
    // The same limit: the ask for G is one member of the request union.
    window.eventHub.$emit('data-alteration-guard-request', {
      requestId,
      guard,
      ...ask,
    } as DataAlterationGuardRequest)
  })
}

// Picks each guard's own fields, so nothing else the answer carries reaches the
// caller.
function verdictOf(
  result: DataAlterationGuardResult
): UnreviewConfirmationVerdict | ResetToDefaultVerdict {
  if (result.guard === 'unreview-confirmation') {
    return { confirmed: result.confirmed, skipUnprepare: result.skipUnprepare }
  }
  return { confirmed: result.confirmed }
}

function guardIsPublished(guard: DataAlterationGuardName): boolean {
  return (document.documentElement.dataset[GUARD_MARKER] ?? '')
    .split(' ')
    .includes(guard)
}

export function publishGuard(guard: DataAlterationGuardName): void {
  publishedGuards.add(guard)
  writeMarker()
}

export function withdrawGuard(guard: DataAlterationGuardName): void {
  publishedGuards.delete(guard)
  writeMarker()
}

// The marker lists every guard that has a decider, so publishing one must not
// drop another. A reader looks for its own name in the list.
function writeMarker(): void {
  if (publishedGuards.size === 0) {
    delete document.documentElement.dataset[GUARD_MARKER]
    return
  }

  document.documentElement.dataset[GUARD_MARKER] = [...publishedGuards].join(
    ' '
  )
}
